import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../translations';
import { useSession } from '../context/SessionContext';
import { UserRole } from '../types';
import { User, Hammer, ChevronRight } from 'lucide-react';

export const SelectionPage: React.FC = () => {
    const navigate = useNavigate();
    const { language, isLoggedIn, role, setRole, feeConfig } = useSession();
    const t = useTranslation(language);

    // Dynamic background configuration
    const isVideo = feeConfig.landingBackgroundType === 'video';
    const backgroundUrl = isVideo ? feeConfig.landingVideoUrl : feeConfig.landingImageUrl;

    // Add cache-busting parameter to force reload when URL changes
    const [imageError, setImageError] = React.useState(false);
    const [mediaKey, setMediaKey] = React.useState(0);
    const cacheBustedUrl = backgroundUrl ? `${backgroundUrl}${backgroundUrl.includes('?') ? '&' : '?'}t=${Date.now()}` : '';

    // Fallback image in case of error
    const fallbackImage = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&h=1080&fit=crop';

    const handleImageError = () => {
        console.error('❌ Failed to load background image:', backgroundUrl);
        setImageError(true);
    };

    // Force reload when background URL or type changes
    React.useEffect(() => {
        console.log('🎨 Selection Page Background Changed:', {
            type: isVideo ? 'video' : 'image',
            url: backgroundUrl,
            mediaKey: mediaKey + 1
        });
        setMediaKey(prev => prev + 1);
        setImageError(false);
    }, [backgroundUrl, isVideo]);

    // Auto-redirect if already logged in with a selected role AND role is stored locally
    React.useEffect(() => {
        const storedRole = localStorage.getItem('hh_role');
        // Only redirect if we are logged in AND the role in context matches the role in storage (meaning intent is confirmed)
        // This allows users on fresh installs (empty storage) to potentially switch roles or see the selection screen
        if (isLoggedIn && role !== UserRole.NONE && storedRole === role) {
            if (role === UserRole.CUSTOMER) navigate('/user');
            else if (role === UserRole.WORKER) navigate('/worker');
        }
    }, [isLoggedIn, role, navigate]);

    const handleSelect = (selectedRole: UserRole) => {
        console.log("Selected Role:", selectedRole);
        setRole(selectedRole);
        localStorage.setItem('hh_role', selectedRole);

        if (selectedRole === UserRole.CUSTOMER) {
            navigate('/user');
        } else {
            navigate('/worker/login');
        }
    };

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 sm:px-6 md:px-8 py-8 relative overflow-hidden safe-top safe-bottom">
            {/* Dynamic Background */}
            <div className="absolute inset-0 z-0">
                {isVideo ? (
                    <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        key={`${backgroundUrl}-${mediaKey}`}
                        className="w-full h-full object-cover object-center opacity-70 animate-fadeIn"
                        onError={() => console.error('❌ Failed to load background video:', backgroundUrl)}
                    >
                        <source src={backgroundUrl} type="video/mp4" />
                    </video>
                ) : (
                    <img
                        src={imageError ? fallbackImage : (cacheBustedUrl || fallbackImage)}
                        onError={handleImageError}
                        key={`${backgroundUrl}-${mediaKey}`}
                        className="w-full h-full object-cover object-center opacity-70 animate-fadeIn"
                        alt="Selection Page Background"
                    />
                )}

                {/* Soft Vignette Overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/70 to-white/90"></div>
            </div>

            {/* Logo and Title - Fully Centered */}
            <div className="mb-8 sm:mb-10 md:mb-12 text-center relative z-10 animate-slideDown w-full max-w-md">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-2xl sm:rounded-3xl mb-4 sm:mb-6 shadow-2xl rotate-3">
                    <User className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
                    Choose Your <span className="text-blue-600">Journey</span>
                </h1>
                <p className="text-slate-400 mt-2 text-[9px] sm:text-[10px] font-black tracking-[0.2em] sm:tracking-[0.3em] uppercase">
                    Select your role
                </p>
            </div>

            {/* Role Options - Centered & Compact */}
            <div className="grid gap-3 sm:gap-4 w-full max-w-[320px] sm:max-w-xs relative z-10 px-4 sm:px-0">
                <button
                    onClick={() => handleSelect(UserRole.CUSTOMER)}
                    className="p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 border-slate-100 bg-white hover:border-blue-200 transition-all flex items-center justify-between group shadow-sm hover:shadow-lg hover:-translate-y-0.5"
                >
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-sm bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all">
                            <User className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div>
                            <h3 className="font-black text-slate-900 text-sm sm:text-base text-left group-hover:text-blue-700 transition-colors">{t.hire_expert}</h3>
                            <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold uppercase tracking-widest text-left">{t.i_need_work}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </button>

                <button
                    onClick={() => handleSelect(UserRole.WORKER)}
                    className="p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 border-slate-100 bg-white hover:border-slate-800 transition-all flex items-center justify-between group shadow-sm hover:shadow-lg hover:-translate-y-0.5"
                >
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center font-black text-sm bg-slate-100 text-slate-700 group-hover:bg-slate-900 group-hover:text-white transition-all">
                            <Hammer className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div>
                            <h3 className="font-black text-slate-900 text-sm sm:text-base text-left group-hover:text-slate-900 transition-colors">{t.i_am_expert}</h3>
                            <p className="text-[8px] sm:text-[9px] text-slate-400 font-bold uppercase tracking-widest text-left">{t.i_want_to_earn}</p>
                        </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-900 transition-colors" />
                </button>
            </div>

            {/* Footer Style - Minimal */}
            <div className="mt-8 sm:mt-12 animate-fadeIn relative z-10">
                <div className="flex items-center gap-2 opacity-50">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-[8px] sm:text-[9px] text-slate-400 font-black uppercase tracking-[0.2em]">Secure Cloud Network</span>
                </div>
            </div>
        </div>
    );
};
